import { CompositionNormalizer } from '@src/components/sidepanel/compositionNormalizer/compositionNormalizer';
import { validateTranslationFlags } from '@src/components/sidepanel/compositionNormalizer/validateTranslationFlags';
import { CompositionPlacement } from '@src/components/sidepanel/CompositionSettings/types';
import { TimelineMode } from '@src/editor/commands/TimelineMode';
import {
  derivePinnedTimelineTarget,
  getContentEndTime,
  resolvePlacementExclusions,
  resolveTimelineScopeBounds,
} from '@src/editor/lib/placementBounds';
import {
  evaluatePlacementEdge,
  isPinnablePlacement,
} from '@src/editor/lib/placementEvaluation';
import { err, ok } from '@src/services/common/Result';
import { applyDefaultControlValuesToSource } from '@src/utility/compositionSettings';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  getNamecardValueOverrides,
  type NamecardSpeakerValues,
} from '@src/utility/namecardCompositionSettings';
import {
  applyQuoteCardSourceModifications,
  getQuoteCardValueOverrides,
} from '@src/utility/quoteCardsCompositionSettings';
import { v4 as uuid } from 'uuid';

import type {
  AITextControlConfiguration,
  CompositionTemplate,
  ControlConfiguration,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { RippleConfig } from '@src/editor/lib/TrackManager';
import type { Element } from '@src/editor/models/Element';
import type { Result } from '@src/services/common/Result';
import type { Showcase, Story } from '@src/types.ts/story';
import type { ExtendedAspectRatio } from '@src/types.ts/video';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type { VideoLike } from '@src/utility/extraElementData';

const logger = LogManager.getLogger('service:compositionElement');

// Per-session dedupe of translation-flag warnings: fire once per unique
// (templateId, warning fingerprint) so re-placing the same template doesn't spam,
// but re-editing the template (changing the warning set) re-fires.
const loggedTranslationFlagWarnings = new Map<string, string>();

/**
 * Recursively regenerates all `id` fields in a composition source object.
 * This prevents duplicate element IDs when creating multiple compositions
 * from the same template (e.g. namecards for different speakers).
 */
function regenerateElementIds(
  source: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...source };

  // Replace the top-level id if present
  if ('id' in result) {
    result.id = uuid();
  }

  // Recursively regenerate IDs in nested elements
  if (Array.isArray(result.elements)) {
    result.elements = result.elements.map((el: Record<string, unknown>) =>
      regenerateElementIds({ ...el }),
    );
  }

  return result;
}

/** Default transition overlap in seconds for SECTION placement */
export interface QuoteCardProps {
  text: string | undefined;
  background: string | undefined;
  name: string | undefined;
  org: string | undefined;
  headshot: string | undefined;
  dimension: ExtendedAspectRatio;
}
export interface CreateCompositionElementOptions {
  template: CompositionTemplate;
  editorStore: EditorStoreLike;
  videoProps: {
    duration: number;
    /**
     * Returns the live current video. Always call this — never destructure
     * or cache. Matches the contract on `AddVideoTemplateOptions['videoProps']`.
     */
    getCurrentVideo: () => VideoLike;
    aspectRatio: ExtendedAspectRatio;
    time: number;
    organization?: Showcase;
    story?: Story;
    convertToVh: (v: string) => string;
  };
  quoteCardProps?: QuoteCardProps;
  /**
   * Resolved speaker values for namecards. When provided, the speaker-bound
   * Name/Title text controls render "Loading…" (or blank when the speaker lacks
   * the field) at creation instead of the template's placeholder text, so the
   * renderer never flashes the template defaults before the async speaker
   * resolve lands.
   */
  namecardValues?: NamecardSpeakerValues;
  /**
   * The namecard SpeakerControl's `controlBindings`, used to locate the bound
   * Name/Title text controls by `control.id`.
   */
  speakerControlBindings?: Record<string, string>;
  upsertExtraElementDataCb: (
    elementId: string,
    template: CompositionTemplate,
  ) => void;
  /**
   * PLAT-10915 — fired once, with every `aiText` control the template declares,
   * after the element exists. This is the single creation path every surface
   * routes through (`addOverlayFromTranscript`, `RightSidePanel`,
   * `useQuoteCardRenderer`, `VideoTemplateService`), which is why the automatic
   * first generation hooks here rather than on the overlay path — intro cards,
   * end cards, nametags and quote cards can all carry an `aiText` control and
   * none of them arrive through that flow (DS1).
   *
   * It is a **side effect of a completed creation, not a step inside it**
   * (DS3): the callback is invoked synchronously, awaited by nothing, and a
   * throw is logged rather than allowed to fail the insert. A composition that
   * failed to appear because a model call failed would be a far worse outcome
   * than a graphic showing its placeholder for a moment.
   *
   * Omitted by surfaces that can't resolve what a control reads — nothing
   * generates there, and the composition behaves exactly as it did before.
   */
  onAITextControlsCreated?: (
    elementId: string,
    controls: ReadonlyArray<AITextControlConfiguration>,
  ) => void;
  createUndoEntry?: boolean;
  /**
   * PLAT-10105 — the transcript selection the insert came from, as an exact
   * `[time, time + duration]` range. The timeline is NOT rippled: the overlay
   * sits on top of existing content exactly like an added photo. When omitted,
   * behaviour is the `duration: 4` default + placement-driven timing.
   *
   * PLAT-10921 step 1 — **this is a default, not an override.** It used to
   * bypass `template.placement` unconditionally, which made placement dead data
   * for every from-transcript insert: an overlay landed on the selection
   * regardless of what its template said. Now a template whose placement binds a
   * timeline edge (`clip` / `start` / `end`) derives its range from that
   * instead, and the selection range is what a non-binding placement
   * (`current_time` / `section`) falls back to. `selection` placement means
   * "the selection IS the binding", so it uses this range too — and the caller
   * records `transcriptAnchor` so the reconciler can re-derive it later.
   *
   * Either way the range is passed with no ripple, and `transcriptAnchor` is
   * still written, so the graphic knows its scope whichever placement won.
   */
  timeRange?: { time: number; duration: number };
  /**
   * PLAT-11034 — how long the composition runs when its placement leaves the END
   * edge free (`clip-start`, `start`, `end`). The placement decides where the
   * PINNED edge sits; this decides how far the free edge runs from it.
   *
   * Applied after the placement-derived range, so it cannot fight the pin. A
   * placement that pins BOTH edges (`clip`) has no free edge to size, and the
   * value is warned about and ignored rather than silently dropped — a `clip`
   * composition's duration IS the clip.
   *
   * The video-template hook is the first caller: a `clip-start` hook runs for the
   * template's authored duration (default 10s) from where the content starts.
   */
  durationOverride?: number;
  /**
   * When true, the created composition element is stamped `locked: true` so the
   * user cannot select/move/resize it on the canvas (Creatomate's native
   * non-interactive flag). Used by the video-template "Lock Position" feature.
   * Only ever ORs a lock ON — never forces a template's own `locked` off.
   */
  locked?: boolean;
}

export interface CreateCompositionElementValue {
  element: Element;
  elementId: string;
}

/**
 * Service orchestration for composition element creation.
 * Responsibilities:
 *  - Aspect ratio source adjustment
 *  - Default control value population (pure helper reuse)
 *  - Placement timing logic (START / END / default current time)
 *  - Meta persistence (template + control set)
 * Does NOT perform UI selection, activation, or speaker resolution (caller coordinates).
 */
export async function createCompositionElement(
  opts: CreateCompositionElementOptions,
): Promise<Result<CreateCompositionElementValue>> {
  const {
    quoteCardProps,
    namecardValues,
    speakerControlBindings,
    template,
    videoProps,
    editorStore,
    upsertExtraElementDataCb,
    onAITextControlsCreated,
    createUndoEntry = true,
    timeRange,
    durationOverride,
    locked,
  } = opts;

  try {
    if (!template || !template.id) {
      return err('VALIDATION', 'Missing template or template id');
    }
    if (!videoProps.getCurrentVideo()) {
      return err('PRECONDITION', 'No active video for composition element');
    }

    // Clone & aspect-ratio adjust
    const adjustedComposition =
      CompositionNormalizer.adjustCompositionSourceByAspectConfiguration(
        template,
        videoProps.aspectRatio,
      );

    // Validate translation flags on the controls; sanitize misconfigured entries
    // in-memory and log warnings once per (templateId, warning set) per session.
    const { sanitized: sanitizedControls, warnings: translationFlagWarnings } =
      validateTranslationFlags(template.controls);
    if (translationFlagWarnings.length > 0) {
      const fingerprint = JSON.stringify(translationFlagWarnings);
      if (loggedTranslationFlagWarnings.get(template.id) !== fingerprint) {
        loggedTranslationFlagWarnings.set(template.id, fingerprint);
        translationFlagWarnings.forEach(warning => {
          logger.warn(`composition_control_${warning.kind}`, {
            templateId: template.id,
            ...warning,
          });
        });
      }
    }

    // Apply default control values into the cloned tree
    applyDefaultControlValuesToSource({
      source: adjustedComposition.source,
      controls: sanitizedControls,
      organization: videoProps.organization,
      story: videoProps.story,
      convertToVh: (v: string) => videoProps.convertToVh(v),
      originalValueOverrides: quoteCardProps
        ? control => getQuoteCardValueOverrides(control, quoteCardProps)
        : namecardValues
          ? control =>
              getNamecardValueOverrides(
                control,
                namecardValues,
                speakerControlBindings,
              )
          : undefined,
      sourceModifications: quoteCardProps
        ? source => applyQuoteCardSourceModifications(source, quoteCardProps)
        : undefined,
    });

    // Regenerate all element IDs in the source to avoid duplicates when
    // the same template is used to create multiple compositions (e.g. namecards).
    const sourceWithFreshIds = regenerateElementIds(adjustedComposition.source);

    const props: Record<string, unknown> = {
      time: videoProps.time,
      templateId: template.id,
      duration: 4,
      ...sourceWithFreshIds,
    };

    // PLAT-10105 / PLAT-10921 step 1 — a from-transcript insert lands on a
    // resolved range: the template's placement when that placement binds a
    // timeline edge, the selection otherwise. Applied AFTER the source spread so
    // a stray top-level `time` / `duration` on the template source can't
    // clobber the authoritative range.
    const resolvedRange = timeRange
      ? resolveInsertTimeRange(timeRange, template, editorStore)
      : resolveCursorInsertTimeRange(videoProps.time, template, editorStore);
    if (resolvedRange) {
      props.time = resolvedRange.time;
      props.duration = resolvedRange.duration;
    }

    // PLAT-11034 — size the FREE edge. After the placement-derived range, so the
    // pin always wins; refused when both edges are pinned, since there is no free
    // edge to size.
    if (durationOverride !== undefined && durationOverride > 0) {
      if (
        evaluatePlacementEdge({
          placement: template.placement,
          edge: 'end',
          pinned: true,
        }) === 'enforce'
      ) {
        logger.warn(
          'Ignoring durationOverride — this placement pins the end edge',
          { templateId: template.id, placement: template.placement },
        );
      } else {
        props.duration = durationOverride;
      }
    }

    // "Lock Position" (video template): stamp the top-level composition element
    // non-interactive. Only ever ORs the lock ON so a template source that is
    // already `locked` stays locked when the config flag is off.
    if (locked) {
      props.locked = true;
    }

    const placement = template?.placement ?? CompositionPlacement.CURRENT_TIME;

    const rippleConfig: RippleConfig = timeRange
      ? // Range-bound overlay: never ripple — it overlays existing content.
        { overlapBefore: 0, overlapAfter: 0 }
      : {
          overlapBefore: [
            CompositionPlacement.SECTION,
            CompositionPlacement.END,
          ].includes(placement)
            ? (template.overlapBefore ?? 0)
            : 0,
          overlapAfter: [
            CompositionPlacement.SECTION,
            CompositionPlacement.START,
          ].includes(placement)
            ? (template.overlapAfter ?? 0)
            : 0,
        };

    const element = executeAddCompositionElementCommand(
      videoProps.duration,
      editorStore,
      props,
      // Bypass placement timing math when range-bound so the element lands at
      // `props.time` in DEFAULT (non-rippling) timeline mode. Still correct
      // after PLAT-10921 step 1 — `resolveInsertTimeRange` has ALREADY consulted
      // the placement, so re-applying it here would double-resolve it.
      resolvedRange ? null : template.placement,
      createUndoEntry,
      videoProps.time,
      rippleConfig,
    );

    if (!element || typeof element.id !== 'string') {
      return err('PRECONDITION', 'Failed to create composition element');
    }

    // Persist meta (template + controls + flags)
    try {
      upsertExtraElementDataCb(element.id, template);
    } catch (metaError) {
      logger.warn('Failed to persist composition meta (continuing)', {
        elementId: element.id,
        ...extractErrorInfo(metaError),
      });
    }

    // PLAT-10921 — the add command above (`executeAddCompositionElementCommand`)
    // ran its reconcile pass BEFORE the line above wrote `placementPinned`, so a
    // `clip` graphic — which lands with the default `duration: 4` and relies on
    // the reconciler to snap both edges to the clip bounds — was skipped as
    // not-yet-pinned and stayed 4s until the next command (a nudge) reconciled
    // it. Re-run reconciliation now that the pin flag is persisted, so the span
    // is correct on insert. Same call the re-pin toggle uses (CompositionControls);
    // gated on pinnable placements so non-binding inserts pay nothing.
    if (isPinnablePlacement(template.placement)) {
      editorStore.editor.reconcilePlacements();
    }

    // PLAT-10915 — the graphic arrives written. Fired here so every insertion
    // path gets it, never awaited, and never able to fail the insert (DS3).
    if (onAITextControlsCreated) {
      const aiTextControls = sanitizedControls.filter(
        isGeneratableAITextControl,
      );
      if (aiTextControls.length > 0) {
        try {
          onAITextControlsCreated(element.id, aiTextControls);
        } catch (aiTextError) {
          logger.warn('AI text generation hook threw (continuing)', {
            elementId: element.id,
            ...extractErrorInfo(aiTextError),
          });
        }
      }
    }

    return ok({ element, elementId: element.id });
  } catch (error) {
    logger.warn('Unhandled exception creating composition element', {
      error,
    });
    return err(
      'UNKNOWN',
      'Unhandled error creating composition element',
      error,
    );
  }
}

/**
 * PLAT-10921 step 1 — the range a from-transcript insert actually lands on.
 *
 * The selection range used to win unconditionally, which made `placement` dead
 * data on this path. Now a template whose placement binds a timeline edge
 * derives its range from that edge, using the SAME derivation the reconciler
 * uses — so the graphic lands where the reconciler would have put it on the next
 * command, instead of visibly jumping there.
 *
 * The selection range is the fallback for every unbound edge and for the
 * placements that bind nothing (`current_time` / `section`), and for
 * `selection` itself — where the selection *is* the binding.
 */
function resolveInsertTimeRange(
  selectionRange: { time: number; duration: number },
  template: CompositionTemplate,
  editorStore: EditorStoreLike,
): { time: number; duration: number } {
  const placement = template.placement;
  if (
    !isPinnablePlacement(placement) ||
    placement === CompositionPlacement.SELECTION
  ) {
    return selectionRange;
  }

  const { tracks } = editorStore.editor.timeline;
  const extraElementData =
    editorStore.editor.getTrackManager().extraElementDataRaw ?? {};
  // The same exclusions the reconciler uses, so the insert lands exactly where
  // the next reconcile would put it rather than visibly jumping there.
  const excluded = resolvePlacementExclusions(tracks, extraElementData);

  const target = derivePinnedTimelineTarget({
    placement,
    compositionType: template.compositionType ?? null,
    scope: resolveTimelineScopeBounds(tracks, extraElementData, excluded),
    contentEndTime: getContentEndTime(tracks, excluded),
    overlapBefore: template.overlapBefore ?? 0,
  });
  if (!target) return selectionRange;

  // An edge the placement doesn't bind keeps the selection's value.
  const selectionEnd = selectionRange.time + selectionRange.duration;
  const time = target.time ?? selectionRange.time;
  const duration = (target.endTime ?? selectionEnd) - time;

  // A degenerate scope (no content yet, or an end card before the intro card
  // ends) must not produce a zero-length graphic. Fall back rather than insert
  // something the creator can't grab on the timeline. The caller decides what
  // the fallback means — the selection for a from-transcript insert, the
  // template's own duration for a cursor one (`resolveCursorInsertTimeRange`).
  if (!(duration > 0)) {
    logger.warn('Placement-derived insert range is empty — falling back', {
      templateId: template.id,
      placement,
      time,
      duration,
    });
    return selectionRange;
  }

  return { time, duration };
}

/**
 * PLAT-10921 — the same derivation for an insert with NO transcript selection.
 *
 * A `clip-start` / `clip-end` graphic added straight from the design grid has
 * only the playhead to go on, and the playhead is exactly the free edge: the
 * graphic runs from the clip's start TO the cursor, or from the cursor to the
 * clip's END. Reading the cursor as a zero-length selection makes that fall out
 * of `resolveInsertTimeRange` unchanged, so the two entry points cannot drift.
 *
 * Every other placement is left alone and keeps landing on the playhead with the
 * template's own duration: `clip` already resolves both edges through the
 * reconciler, and a cursor is not a selection for the placements that want one.
 *
 * A cursor sitting on the bound edge (playhead at or before the clip start for
 * `clip-start`) would derive an empty graphic. That returns `undefined` so the
 * insert falls back to the default duration at the playhead, and the reconciler
 * pulls the bound edge into place on its next pass.
 */
function resolveCursorInsertTimeRange(
  cursorTime: number,
  template: CompositionTemplate,
  editorStore: EditorStoreLike,
): { time: number; duration: number } | undefined {
  const placement = template.placement;
  if (
    placement !== CompositionPlacement.CLIP_START &&
    placement !== CompositionPlacement.CLIP_END
  ) {
    return undefined;
  }

  const range = resolveInsertTimeRange(
    { time: cursorTime, duration: 0 },
    template,
    editorStore,
  );
  return range.duration > 0 ? range : undefined;
}

/**
 * PLAT-10915 — an `aiText` control the generation hook can actually run.
 * Provenance is keyed by `id` (E5) and there is nothing to call without a
 * `promptId`, so a control missing either is skipped; it still renders as a
 * plain hand-editable text row, exactly as `extractCompositionQuickEditFields`
 * degrades it.
 */
function isGeneratableAITextControl(
  control: ControlConfiguration,
): control is AITextControlConfiguration {
  return control.type === 'aiText' && !!control.id && !!control.promptId;
}

/**
 * Finds the video clip (element) at a given playhead time on the video transcript track.
 * @returns The element at the given time, or null if no clip is found
 */
function findCurrentClipAtTime(
  editorStore: EditorStoreLike,
  currentTime: number,
): Element | null {
  const transcriptTrack = editorStore.editor.getVideoTranscriptTrack();
  if (!transcriptTrack) {
    return null;
  }
  return transcriptTrack.findElementAtTime(currentTime);
}

/**
 * Calculates the insertion time for SECTION placement.
 * Returns the end of the current clip minus the transition overlap,
 * or indicates if the clip is at the timeline end (should use END behavior).
 *
 * Special case: If the current clip is the LAST clip and there's a previous clip,
 * insert between the previous and current clip instead of at the end.
 */
export function calculateSectionInsertionTime(
  editorStore: EditorStoreLike,
  currentTime: number,
  timelineDuration: number,
  overlapBefore: number = 0,
): { insertionTime: number; isAtEnd: boolean } | null {
  const transcriptTrack = editorStore.editor.getVideoTranscriptTrack();
  const currentClip = findCurrentClipAtTime(editorStore, currentTime);

  if (!currentClip || !transcriptTrack) {
    return null;
  }

  const clipEndTime = currentClip.end_time;
  const epsilon = 0.001;

  // Check if the clip ends at or very near the timeline end (last clip)
  const isLastClip =
    Math.abs(clipEndTime - timelineDuration) < epsilon ||
    clipEndTime >= timelineDuration;

  if (isLastClip) {
    // Check if there's a previous clip using TranscriptTrack method
    const previousClip = transcriptTrack.getPreviousVideoTranscriptElement(
      currentClip.id,
    );

    if (previousClip) {
      // Insert between previous and current (last) clip
      const previousClipEnd = previousClip.end_time;
      const insertionTime = Math.max(0, previousClipEnd - overlapBefore);
      logger.info(
        'SECTION placement: last clip with previous, inserting between clips',
        { previousClipEnd, insertionTime },
      );
      return { insertionTime, isAtEnd: false };
    }

    // No previous clip - this is the only clip, use END behavior
    return { insertionTime: timelineDuration, isAtEnd: true };
  }

  // Not the last clip - insert at current clip's end minus overlap
  const insertionTime = Math.max(0, clipEndTime - overlapBefore);

  return { insertionTime, isAtEnd: false };
}

function executeAddCompositionElementCommand(
  duration: number,
  editorStore: EditorStoreLike,
  props: Record<string, unknown>,
  placement?: CompositionPlacement | null,
  createUndoEntry: boolean = true,
  currentTime?: number,
  rippleConfig?: RippleConfig,
): Element | null {
  let timelineMode = TimelineMode.DEFAULT;

  if (placement === CompositionPlacement.END) {
    if (typeof duration === 'number' && !Number.isNaN(duration)) {
      props.time = Math.max(0, duration - (rippleConfig?.overlapBefore ?? 0));
    }
  } else if (placement === CompositionPlacement.START) {
    props.time = 0;
    timelineMode = TimelineMode.RIPPLE_ALL_TRACKS;
  } else if (placement === CompositionPlacement.SECTION) {
    const playheadTime = currentTime ?? (props.time as number) ?? 0;
    const sectionResult = calculateSectionInsertionTime(
      editorStore,
      playheadTime,
      duration,
      rippleConfig?.overlapBefore ?? 0,
    );

    if (!sectionResult) {
      // No clip found at current time, fall back to CURRENT_TIME behavior
      logger.info('SECTION placement: no clip at playhead, using current time');
    } else if (sectionResult.isAtEnd) {
      // Clip is at the end, behave like END placement (no overlap since we're appending)
      props.time = duration;
      logger.info('SECTION placement: clip at end, using END behavior');
    } else {
      // Insert at calculated section point with ripple (accounting for overlaps)
      // Card overlaps previous clip by startOverlap, next clip overlaps card by endOverlap
      // Music before insertionTime is NOT pushed; music at/after IS pushed
      props.time = sectionResult.insertionTime;
      timelineMode = TimelineMode.RIPPLE_ALL_TRACKS;
      logger.info('SECTION placement: inserting at clip end with ripple', {
        insertionTime: sectionResult.insertionTime,
      });
    }
  }

  return editorStore.editor.commandCreateCompositionElement(
    props,
    undefined,
    timelineMode,
    createUndoEntry,
    rippleConfig,
  );
}
